import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/socket';
import { useMarketStore } from '@/store/marketStore';
import { useNotifStore } from '@/store/notifStore';
import { usePointStore } from '@/store/pointStore';
import { useAuthStore } from '@/store/authStore';
import { useOnlineStore } from '@/store/onlineStore';
import { queryKeys } from '@/api/queryKeys';
import { get } from '@/api/client';

/**
 * 앱 전역 Socket.io 연결 — Layout에서 한 번 호출
 * - price:global → marketStore 업데이트
 * - notification:new → notifStore 증가 + 리스트 쿼리 무효화
 * - point:earned → toast 큐 추가
 * - 로그인 시 /notifications/unread-count 로 초기 배지 동기화
 */
export function useAppSocket() {
  const applyPriceDelta = useMarketStore((s) => s.applyPriceDelta);
  const incrementUnread = useNotifStore((s) => s.incrementUnread);
  const setUnreadCount = useNotifStore((s) => s.setUnreadCount);
  const addPointToast = usePointStore((s) => s.addToQueue);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setOnlineCount = useOnlineStore((s) => s.setCount);
  const queryClient = useQueryClient();

  // 로그인 상태에서 초기 unread 카운트 동기화 (60초마다 재검증)
  const { data: unreadData } = useQuery({
    queryKey: queryKeys.notifications.unread,
    queryFn: () => get<{ count: number }>('/notifications/unread-count'),
    enabled: isLoggedIn,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (unreadData) setUnreadCount(unreadData.count);
  }, [unreadData, setUnreadCount]);

  useEffect(() => {
    const socket = getSocket();

    socket.on('price:global', (tick: { ticker: string; price: number; changePct: number }) => {
      // name/assetType 은 보존 — Socket 이벤트는 price/changePct 만 업데이트
      applyPriceDelta({
        ticker: tick.ticker,
        price: tick.price,
        changePct: tick.changePct,
      });
    });

    socket.on('notification:new', () => {
      incrementUnread();
      // 알림 목록이 열려 있으면 자동 갱신
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread });
    });

    socket.on('point:earned', (data: { amount: number; action: string }) => {
      addPointToast(data.amount, data.action);
    });

    socket.on('surge:alert', (data: { ticker: string; changePct: number; postId: number }) => {
      console.log('[Surge]', data);
    });

    socket.on('online:count', (data: { members: number; guests: number }) => {
      setOnlineCount(data.members ?? 0, data.guests ?? 0);
    });

    return () => {
      socket.off('price:global');
      socket.off('notification:new');
      socket.off('point:earned');
      socket.off('surge:alert');
      socket.off('online:count');
    };
  }, [applyPriceDelta, incrementUnread, addPointToast, setOnlineCount, queryClient]);
}

/**
 * 종목 상세 페이지용 — 특정 ticker 실시간 구독
 */
export function useTickerSubscription(
  ticker: string | undefined,
  onTick: (tick: { ticker: string; price: number; changePct: number }) => void,
) {
  useEffect(() => {
    if (!ticker) return;
    const socket = getSocket();
    socket.emit('subscribe:ticker', ticker);
    socket.on('price:tick', onTick);
    return () => {
      socket.emit('unsubscribe:ticker', ticker);
      socket.off('price:tick', onTick);
    };
  }, [ticker, onTick]);
}

/**
 * 게시글 상세 페이지용 — 댓글 실시간 구독
 */
export function usePostSubscription(
  postId: number | undefined,
  onCommentAdded: (comment: any) => void,
) {
  useEffect(() => {
    if (!postId) return;
    const socket = getSocket();
    socket.emit('subscribe:post', postId);
    socket.on('comment:added', onCommentAdded);
    return () => {
      socket.emit('unsubscribe:post', postId);
      socket.off('comment:added', onCommentAdded);
    };
  }, [postId, onCommentAdded]);
}
